import { Skeleton } from "design-system/atoms/Skeleton"
import { getMixedPalette } from "design-system/lib/chart-utils"
import { Card } from "design-system/molecules/Card"
import { ChartConfig, ChartContainer } from "design-system/molecules/Chart"
import { NoData } from "design-system/molecules/NoData"
import { useMemo } from "react"
import { Tooltip as RechartsTooltip, Treemap, TreemapNode } from "recharts"

const chartConfig = {} satisfies ChartConfig
const CELL_PADDING = 2
const CAPABILITY_TREEMAP_PALETTE = getMixedPalette(undefined, true)

const getTreemapFill = (index: number) => {
  const color = CAPABILITY_TREEMAP_PALETTE[index % CAPABILITY_TREEMAP_PALETTE.length] ?? "muted"
  return `var(--${color})`
}

type TreemapNodeData = {
  id: string
  label: string
  count: number
  percentage: number
  fill: string
}

type Props = {
  data: Array<{ id: string; label: string; count: number }> | undefined
  isPending: boolean
  title: string
  description: string
  emptyTitle: string
  emptyDescription: string
  itemLabel: string
  countLabel: string
  percentageLabel: string
  onItemClick?: (id: string) => void
}

const truncateLabel = (label: string, width: number) => {
  const maxCharacters = Math.max(Math.floor((width - 20) / 7), 0)
  if (label.length <= maxCharacters) return label
  if (maxCharacters <= 1) return ""
  return `${label.slice(0, maxCharacters - 1)}…`
}

export function CapabilityDistributionTreemap({
  data,
  isPending,
  title,
  description,
  emptyTitle,
  emptyDescription,
  itemLabel,
  countLabel,
  percentageLabel,
  onItemClick,
}: Props) {
  const chartData = useMemo(() => {
    const distribution = (data ?? []).filter((item) => item.count > 0)
    const total = distribution.reduce((sum, item) => sum + item.count, 0)

    return distribution.map((item, index) => ({
      ...item,
      count: item.count,
      percentage: total === 0 ? 0 : (item.count / total) * 100,
      fill: getTreemapFill(index),
    }))
  }, [data])

  const hasData = chartData.length > 0

  return (
    <Card className="p-4">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-0.5">
          <span className="text-md font-medium">{title}</span>
          <span className="text-sm text-muted-foreground">{description}</span>
        </div>
        <div className="relative h-[280px]">
          {isPending && <Skeleton className="h-full w-full" />}
          {!isPending && hasData && (
            <ChartContainer config={chartConfig} className="h-full w-full">
              <Treemap
                data={chartData}
                dataKey="count"
                nameKey="label"
                content={<CapabilityDistributionTreemapContent onItemClick={onItemClick} />}
                isAnimationActive={false}
              >
                <RechartsTooltip
                  content={
                    <CapabilityDistributionTooltip
                      itemLabel={itemLabel}
                      countLabel={countLabel}
                      percentageLabel={percentageLabel}
                    />
                  }
                />
              </Treemap>
            </ChartContainer>
          )}
          {!isPending && !hasData && (
            <NoData frame className="h-full" title={emptyTitle} description={emptyDescription} />
          )}
        </div>
      </div>
    </Card>
  )
}

function CapabilityDistributionTooltip({
  active,
  payload,
  itemLabel,
  countLabel,
  percentageLabel,
}: {
  active?: boolean
  payload?: Array<{ payload?: TreemapNodeData }>
  itemLabel: string
  countLabel: string
  percentageLabel: string
}) {
  const node = payload?.[0]?.payload

  if (!active || !node) return null

  return (
    <Card className="pointer-events-none min-w-52 p-3 shadow-lg">
      <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
        <span className="text-muted-foreground">{itemLabel}</span>
        <span className="font-medium">{node.label}</span>
        <span className="text-muted-foreground">{countLabel}</span>
        <span className="font-medium">{node.count}</span>
        <span className="text-muted-foreground">{percentageLabel}</span>
        <span className="font-medium">{`${node.percentage.toFixed(1)}%`}</span>
      </div>
    </Card>
  )
}

function CapabilityDistributionTreemapContent({
  onItemClick,
  ...props
}: Partial<TreemapNode> & { onItemClick?: (id: string) => void }) {
  const { x = 0, y = 0, width = 0, height = 0, depth = 0 } = props
  const node = props as TreemapNodeData
  if (depth === 0 || !node.id) return null

  const label = truncateLabel(node.label, width)
  const innerWidth = Math.max(width - CELL_PADDING * 2, 0)
  const innerHeight = Math.max(height - CELL_PADDING * 2, 0)

  return (
    <g
      aria-label={node.label}
      className={onItemClick ? "cursor-pointer" : undefined}
      onClick={() => onItemClick?.(node.id)}
    >
      <rect
        x={x + CELL_PADDING}
        y={y + CELL_PADDING}
        width={innerWidth}
        height={innerHeight}
        fill={node.fill}
        stroke="var(--background)"
        strokeWidth={1}
        rx={3}
        className="opacity-70 transition-opacity hover:opacity-100"
      />
      {label && innerHeight >= 36 && (
        <text
          x={x + 12}
          y={y + 24}
          fill="var(--foreground)"
          fontSize={12}
          fontWeight={500}
          pointerEvents="none"
        >
          {label}
        </text>
      )}
    </g>
  )
}
